import { useCallback, useEffect, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { milestonesAPI, tasksAPI } from '../../lib/api';
import { getUser } from '../../lib/auth';
import { useProject } from '../../layout/ProjectLayout';
import { addDays, diffDays, todayISO } from '../../lib/dates';
import { Field, Modal, btnGhost, btnPrimary, fmtDate, inputCls } from '../../components/ui';

function MilestoneModal({ milestone, groups, onClose, onSaved, projectId }) {
  const [name, setName] = useState(milestone?.name || '');
  const [startDate, setStartDate] = useState(milestone?.start_date || '');
  const [dueDate, setDueDate] = useState(milestone?.due_date || '');
  const [groupIds, setGroupIds] = useState(milestone?.group_ids || []);
  const [busy, setBusy] = useState(false);

  const toggleGroup = (gid) =>
    setGroupIds((prev) => (prev.includes(gid) ? prev.filter((x) => x !== gid) : [...prev, gid]));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const data = { name, start_date: startDate || null, due_date: dueDate || null, group_ids: groupIds };
    try {
      if (milestone) await milestonesAPI.update(milestone.id, data);
      else await milestonesAPI.create(projectId, data);
      onSaved();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={milestone ? 'Edit milestone' : 'New milestone'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus className={inputCls} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start date">
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputCls} />
          </Field>
          <Field label="Due date">
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputCls} />
          </Field>
        </div>
        <div>
          <span className="mb-1.5 block text-sm text-pm-muted">Connected task groups (drive progress)</span>
          <div className="flex flex-wrap gap-1.5">
            {groups.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => toggleGroup(g.id)}
                className={`rounded-full border px-2.5 py-1 text-xs ${
                  groupIds.includes(g.id)
                    ? 'border-pm-accent text-pm-accent'
                    : 'border-pm-border text-pm-muted hover:text-pm-text'
                }`}
              >
                {g.name}
              </button>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={btnGhost}>
            Cancel
          </button>
          <button type="submit" disabled={busy || !name.trim()} className={btnPrimary}>
            {milestone ? 'Save' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default function RoadmapScreen() {
  const { project } = useProject();
  const [milestones, setMilestones] = useState([]);
  const [groups, setGroups] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | milestone
  const user = getUser();
  const readOnly = user?.role === 'guest';

  const load = useCallback(async () => {
    const [m, g] = await Promise.all([milestonesAPI.list(project.id), tasksAPI.groups(project.id)]);
    setMilestones(m.data.milestones);
    setGroups(g.data.groups);
  }, [project.id]);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (m) => {
    if (!window.confirm(`Delete milestone "${m.name}"?`)) return;
    await milestonesAPI.remove(m.id);
    await load();
  };

  // gantt-lite: bars across the union range
  const dated = milestones.filter((m) => m.start_date || m.due_date);
  const today = todayISO();
  let rangeStart = today;
  let rangeEnd = today;
  dated.forEach((m) => {
    const s = m.start_date || m.due_date;
    const e = m.due_date || m.start_date;
    if (s < rangeStart) rangeStart = s;
    if (e > rangeEnd) rangeEnd = e;
  });
  rangeStart = addDays(rangeStart, -7);
  rangeEnd = addDays(rangeEnd, 14);
  const total = diffDays(rangeStart, rangeEnd) + 1;

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-xs text-pm-faint">
          Milestone progress is computed live from the tasks in its connected groups.
        </p>
        {!readOnly && (
          <button type="button" onClick={() => setEditing('new')} className={`${btnPrimary} inline-flex items-center gap-1.5`}>
            <Plus size={14} />
            New milestone
          </button>
        )}
      </div>

      {milestones.length === 0 && (
        <div className="rounded-lg border border-dashed border-pm-border py-16 text-center text-sm text-pm-muted">
          No milestones yet.
        </div>
      )}

      <div className="space-y-3">
        {milestones.map((m) => {
          const s = m.start_date || m.due_date;
          const e = m.due_date || m.start_date;
          const left = s ? (diffDays(rangeStart, s) / total) * 100 : 0;
          const w = s ? ((diffDays(s, e) + 1) / total) * 100 : 0;
          return (
            <div key={m.id} className="group rounded-lg border border-pm-border bg-pm-surface p-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">{m.name}</span>
                <span className="text-xs text-pm-faint">
                  {m.start_date && fmtDate(m.start_date)}
                  {(m.start_date || m.due_date) && ' → '}
                  {m.due_date && fmtDate(m.due_date)}
                </span>
                <span className="ml-auto text-xs text-pm-muted">
                  {m.done_tasks}/{m.total_tasks} tasks · {m.progress}%
                </span>
                {!readOnly && (
                  <>
                    <button type="button" onClick={() => setEditing(m)} className="rounded p-1 text-pm-faint opacity-0 hover:text-pm-text group-hover:opacity-100">
                      <Pencil size={13} />
                    </button>
                    <button type="button" onClick={() => remove(m)} className="rounded p-1 text-pm-faint opacity-0 hover:text-pm-danger group-hover:opacity-100">
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </div>
              {/* progress */}
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-pm-raised">
                <div className="h-full rounded-full" style={{ width: `${m.progress}%`, background: project.color }} />
              </div>
              {/* position bar on the shared range */}
              {s && (
                <div className="relative mt-2 h-2 rounded bg-pm-bg">
                  {today >= rangeStart && today <= rangeEnd && (
                    <div className="absolute top-0 h-full w-px" style={{ left: `${(diffDays(rangeStart, today) / total) * 100}%`, background: 'var(--pm-accent)' }} />
                  )}
                  <div className="absolute top-0 h-full rounded" style={{ left: `${left}%`, width: `${Math.max(w, 0.5)}%`, background: `${project.color}88` }} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {editing && (
        <MilestoneModal
          milestone={editing === 'new' ? null : editing}
          groups={groups}
          projectId={project.id}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
